import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import AssistantResultTableSchema, {
  AssistantResultCellParams,
  AssistantResultColumnParams,
  AssistantResultTableJSON,
  AssistantResultTableParams,
} from "ai-assistant/result-tables/assistant-result-table-schema";

/**
 * A table a lookup answered with, which the assistant panel draws as a card under the answer.
 *
 * Its figures are what the lookup counted, written by the server - not retyped by the model, whose
 * answer refers to the card instead (#6223). A cell names a person or a record; where the reader may
 * open that person's or record's page, the cell carries the route to it.
 */
export default class AssistantResultTable {
  public static create(values: AssistantResultTableParams): AssistantResultTable {
    const parsed = AssistantResultTableSchema.validate(values);
    if (isNullOrUndefined(parsed)) {
      throw new Error("Invalid AssistantResultTable parameters.");
    }
    return new AssistantResultTable(parsed);
  }

  public static fromJSON(json: AssistantResultTableJSON): AssistantResultTable {
    if (isNullOrUndefined(json)) return json;
    return new AssistantResultTable(AssistantResultTableSchema.deserialize(json));
  }

  public readonly title: string;
  /** The one figure the rows are about, when there is one. */
  public readonly summary: string | undefined;
  public readonly columns: AssistantResultColumnParams[];
  public readonly rows: AssistantResultCellParams[][];
  /** Rows the server matched and did not send, because a card stops at a fixed number. */
  public readonly rowsNotShown: number;
  /** What the figures were measured over. */
  public readonly note: string | undefined;

  public constructor(params: AssistantResultTableParams) {
    this.title = params.title;
    this.summary = params.summary;
    this.columns = params.columns;
    this.rows = params.rows;
    this.rowsNotShown = params.rowsNotShown;
    this.note = params.note;
  }

  public toJSON(): AssistantResultTableJSON {
    return AssistantResultTableSchema.serialize(this);
  }

  public isEqualTo(other: AssistantResultTable): boolean {
    return JSON.stringify(this.toJSON()) === JSON.stringify(other.toJSON());
  }

  public toString(): string {
    return [this.title, ...this.rows.map((row) => row.map((cell) => cell.text).join(" | "))].join("\n");
  }
}
